// services/api/yokAtlasService.ts
import axios, { AxiosError, AxiosRequestConfig } from "axios";

// YÖK Atlas API yanıtı için tip tanımı
export interface YokAtlasResponse {
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  source: string;
  data: any[][];
}

export interface YokAtlasProgramResponse {
  recordsTotal: number;
  recordsFiltered: number;
  data: YokAtlasProgram[];
}

// Dönüştürülmüş program verisi için tip tanımı
export interface YokAtlasProgram {
  programKodu: string;
  universiteAdi: string;
  fakulteAdi: string;
  bolumAdi: string;
  programTuru: string;
  sehir: string;
  universiteTuru: string;
  ucret: string;
  ogretimTuru: string;
  kontenjan: string;
  yerlesen: string;
  doluluk: string;
  taban: number;
  siralama: number;
  detayUrl: string;
  kosullarUrl: string;
}

// İstek parametreleri için tip tanımı
export interface YokAtlasTercihRobotuRequestParams {
  programTuru: "onlisans" | "lisans";
  puanTuru?: string;
  sehirler?: string[];
  universiteTuru?: ("devlet" | "vakif")[];
  minPuan?: number;
  maxPuan?: number;
  minSiralama?: number;
  maxSiralama?: number;
  bolumAdi?: string;
  bursTuru?: ("ucretli" | "burslu" | "indirimli")[];
  ogretimTuru?: ("orgun" | "ikinci" | "uzaktan" | "acik")[];
  dolulukDurumu?: ("doldu" | "dolmadi")[];
  limit?: number;
  page?: number;
}

// HTML içeriğinden metin çıkarma yardımcı fonksiyonu
function extractTextFromHtml(html: string): string {
  if (!html) return "";
  // HTML etiketlerini kaldır ve özel karakterleri düzelt
  return html
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
}

function extractKontenjan(html: string): string {
  if (!html) return "";
  const match = html.match(
    /title='Kontenjan = Genel \+ Okul Birincisi \+ Şehit-Gazi Yakını \+ 34 Yaş Üstü Kadın \+ Depremzede Aday'>([^<]+)<\/font>/
  );
  return match ? match[1].trim() : "";
}

function extractDoluluk(html: string): string {
  if (!html) return "";
  return html === "Doldu" ? "Doldu" : "Dolmadı";
}

// HTML içinden bağlantı URL'sini çıkarma
function extractUrlFromHtml(html: string, attribute: string = "href"): string {
  if (!html) return "";
  const match = html.match(new RegExp(`${attribute}=['"]([^'"]+)['"]`));
  return match ? match[1] : "";
}

// HTML içinden program kodunu çıkarma
function extractProgramKodu(html: string): string {
  if (!html) return "";
  const match = html.match(/y=(\d+)/);
  return match ? match[1] : "";
}

// HTML içinden puanı çıkarma
function extractPuan(html: string): number {
  if (!html) return 0;
  const match = html.match(/<font color='blue'>([^<]+)<\/font>/);
  if (match) {
    const puan = match[1].replace(",", ".").trim();
    return parseFloat(puan) || 0;
  }
  return 0;
}

// HTML içinden sıralamayı çıkarma
function extractSiralama(html: string): number {
  if (!html) return 0;
  const match = html.match(/<font color='blue'>([^<]+)<\/font>/);
  if (match) {
    // Nokta ve virgülleri kaldır, sadece sayıları al
    const siralama = match[1].replace(/\./g, "").replace(/,/g, "").trim();
    return parseInt(siralama) || 0;
  }
  return 0;
}

// YÖK Atlas API'sine istek gönderen fonksiyon
export const getYokAtlasTercihRobotu = async (
  params: YokAtlasTercihRobotuRequestParams
): Promise<YokAtlasProgramResponse> => {
  try {
    // Kendi backend proxy'nize istek gönderme
    const response = await axios.post<YokAtlasResponse>(
      "http://localhost:5000/api/yok-atlas", // Backend proxy URL'iniz
      params
    );

    // Yanıtı işleme ve dönüştürme
    const programs: YokAtlasProgram[] = response.data.data.map((row: any[]) => {
      const programKodu = extractProgramKodu(row[1]);
      const detayUrl =
        params.programTuru === "onlisans"
          ? `https://yokatlas.yok.gov.tr/onlisans.php?y=${programKodu}`
          : `https://yokatlas.yok.gov.tr/lisans.php?y=${programKodu}`;
      const kosullarUrl =
        params.programTuru === "onlisans"
          ? `https://yokatlas.yok.gov.tr/content/lightbox/kosullar-onlisans.php?y=${programKodu}`
          : `https://yokatlas.yok.gov.tr/content/lightbox/kosullar-lisans.php?y=${programKodu}`;

      // Kontenjan bilgisini düzgün çıkar
      const kontenjan = extractKontenjan(row[10]);

      return {
        programKodu,
        universiteAdi: extractTextFromHtml(row[2]), // Üniversite adı
        fakulteAdi: extractTextFromHtml(row[3]), // Fakülte adı
        bolumAdi: extractTextFromHtml(row[4]), // Bölüm adı
        programTuru: extractTextFromHtml(row[5]), // Program türü
        sehir: row[6], // Şehir
        universiteTuru: row[7], // Üniversite türü (Devlet/Vakıf)
        ucret: row[8], // Ücret durumu
        ogretimTuru: row[9], // Öğretim türü
        kontenjan, // Kontenjan
        yerlesen: extractTextFromHtml(row[16]), // Yerleşen sayısı
        doluluk: extractDoluluk(row[17]), // Doluluk durumu
        taban: extractPuan(row[20]), // Taban puanı
        siralama: extractSiralama(row[21]), // Başarı sıralaması
        detayUrl,
        kosullarUrl,
      };
    });

    // Filtreleme işlemleri
    let filteredPrograms = programs;

    // Bölüm adı araması
    if (params.bolumAdi) {
      const searchTerm = params.bolumAdi.toLowerCase();
      filteredPrograms = filteredPrograms.filter((program) =>
        program.bolumAdi.toLowerCase().includes(searchTerm)
      );
    }

    // Burs türü filtresi
    if (params.bursTuru && params.bursTuru.length > 0) {
      filteredPrograms = filteredPrograms.filter((program) => {
        const programTuru = program.programTuru.toLowerCase();
        if (
          programTuru.includes("burslu") &&
          params.bursTuru!.includes("burslu")
        ) {
          return true;
        }
        if (
          programTuru.includes("indirimli") &&
          params.bursTuru!.includes("indirimli")
        ) {
          return true;
        }
        if (
          program.ucret === "Ücretli" &&
          params.bursTuru!.includes("ucretli")
        ) {
          return true;
        }
        return false;
      });
    }

    // Doluluk durumu filtresi
    if (params.dolulukDurumu && params.dolulukDurumu.length > 0) {
      filteredPrograms = filteredPrograms.filter((program) => {
        if (
          program.doluluk === "Doldu" &&
          params.dolulukDurumu!.includes("doldu")
        ) {
          return true;
        }
        if (
          program.doluluk === "Dolmadı" &&
          params.dolulukDurumu!.includes("dolmadi")
        ) {
          return true;
        }
        return false;
      });
    }

    // Sıralama aralığı filtresi (client-side)
    if (params.minSiralama !== undefined) {
      filteredPrograms = filteredPrograms.filter(
        (program) => program.siralama >= params.minSiralama!
      );
    }
    if (params.maxSiralama !== undefined) {
      filteredPrograms = filteredPrograms.filter(
        (program) => program.siralama <= params.maxSiralama!
      );
    }

    return {
      recordsTotal: response ? response.data.recordsTotal : 0,
      recordsFiltered: response ? response.data.recordsFiltered : 0,
      data: filteredPrograms,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      console.error("YÖK Atlas API Error:", axiosError.response?.data);
      throw new Error(`YÖK Atlas verileri alınamadı: ${axiosError.message}`);
    }
    throw new Error("YÖK Atlas verileri alınırken beklenmeyen bir hata oluştu");
  }
};
